import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  Polygon
} from '@react-google-maps/api';
import { Box, Typography, Paper, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { LocationOn as LocationIcon, Close as CloseIcon } from '@mui/icons-material';
import { FaDrawPolygon as PolygonIcon } from 'react-icons/fa';

const containerStyle = {
  width: '100%',
  height: '600px',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  position: 'relative'
};

const drawingPolygonOptions = {
  fillColor: '#1976d2',
  fillOpacity: 0.15,
  strokeWeight: 2,
  strokeColor: '#1976d2',
  clickable: false,
  editable: false,
  zIndex: 10
};

const defaultCenter = {
  lat: -34.6037,  // Default to Buenos Aires coordinates
  lng: -58.3816
};

/**
 * Map component that displays a Google Map with markers and polygon drawing
 * @param {Object} props - Component props
 * @param {Array} [props.locations=[]] - Array of location objects with lat and lng properties
 * @param {Object} [props.center=defaultCenter] - Center coordinates for the map
 * @param {number} [props.zoom=12] - Initial zoom level
 * @param {Function} [props.onMarkerClick] - Callback function when a marker is clicked
 * @param {Function} [props.onPolygonComplete] - Callback when a polygon drawing is completed
 * @param {Function} [props.onPolygonSelect] - Callback when a polygon is selected
 * @param {boolean} [props.drawingMode=false] - Whether the map is in drawing mode
 * @param {Function} [props.onDrawingModeChange] - Callback when drawing mode changes
 * @param {Array} [props.polygons=[]] - Array of polygons to display
 * @param {Object} [props.selectedPolygon=null] - Currently selected polygon object
 * @param {Array} [props.availableChannels=[]] - Array of available channel names for legend
 * @param {Object} [props.channelColors={}] - Object mapping channel names to colors
 * @param {string} [props.height='600px'] - Height of the map container
 * @param {string} [props.width='100%'] - Width of the map container
 * @param {Object} [props.mapContainerStyle={}] - Custom styles for the map container
 * @param {Array<string>} [props.enabledPlaceTypes=[]] - Google Places types enabled to display
 * @returns {JSX.Element} - Rendered map component
 */
// Move the error UI to a separate component
const MapError = ({ error, width, height, mapContainerStyle }) => (
  <Box sx={{
    width,
    height,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    p: 3,
    textAlign: 'center',
    ...mapContainerStyle
  }}>
    <Typography variant="h6" color="error" gutterBottom>
      Error al cargar el mapa
    </Typography>
    <Typography variant="body2" color="text.secondary">
      No se pudo cargar Google Maps. Por favor, verifica tu conexión a internet.
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
      Error: {error?.message || 'Error desconocido'}
    </Typography>
  </Box>
);

// Main Map component
const Map = ({ 
  locations = [], 
  center = defaultCenter, 
  zoom = 12,
  onMarkerClick,
  onPolygonComplete,
  onPolygonSelect,
  drawingMode = false,
  onDrawingModeChange,
  polygons = [],
  selectedPolygon = null,
  availableChannels = [],
  channelColors = {},
  height = '600px',
  width = '100%',
  mapContainerStyle = {},
  enabledPlaceTypes = [],
  ...rest 
}) => {
  // API Key and loading state
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  // State and refs - all hooks must be called unconditionally
  const [map, setMap] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentPolygon, setCurrentPolygon] = useState(null);
  const [drawingPath, setDrawingPath] = useState([]); // points collected during manual drawing
  const polygonRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(drawingMode);
  const [apiError, setApiError] = useState(null);
  const [placesByType, setPlacesByType] = useState({});
  const lastFetchKeyRef = useRef('');
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: ['places', 'geometry'],
    version: 'weekly',
    onLoad: () => {
      console.log('Google Maps API loaded successfully');
      console.log('Google Maps version:', window.google?.maps?.version);
    },
    onError: (error) => {
      console.error('Error loading Google Maps API:', error);
      setApiError(error);
    }
  });

  // Get Google Maps instance safely
  const getGoogleMaps = useCallback(() => {
    return window.google && window.google.maps ? window.google.maps : null;
  }, []);

  // Handle API load errors
  useEffect(() => {
    if (loadError) {
      console.error('Error loading Google Maps API:', loadError);
      setApiError(loadError);
    }
  }, [loadError]);

  // Sync drawing mode with prop
  useEffect(() => {
    setIsDrawing(drawingMode);
  }, [drawingMode]);

  // Define all callbacks with useCallback
  const handleMarkerClick = useCallback((location, index, event) => {
    setSelectedLocation(location);
    if (onMarkerClick) {
      onMarkerClick(location, index, event);
    }
  }, [onMarkerClick]);

  // Manual polygon drawing: add point on map click
  const handleMapClick = useCallback((event) => {
    if (!isDrawing) return;
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setDrawingPath(prev => [...prev, { lat, lng }]);
  }, [isDrawing]);

  // Complete polygon on double-click (remove last point added by the preceding click)
  const handleMapDblClick = useCallback((event) => {
    if (!isDrawing) return;
    setDrawingPath(prev => {
      // The click event that fires before dblclick already added a point, remove it
      const path = prev.length > 0 ? prev.slice(0, -1) : prev;
      if (path.length >= 3 && onPolygonComplete) {
        onPolygonComplete(path);
        onDrawingModeChange?.(false);
      }
      return [];
    });
  }, [isDrawing, onPolygonComplete, onDrawingModeChange]);

  // Clear drawing path when drawing mode is turned off from parent
  useEffect(() => {
    if (!drawingMode) {
      setDrawingPath([]);
    }
  }, [drawingMode]);

  const toggleDrawing = useCallback(() => {
    onDrawingModeChange?.(!isDrawing);
  }, [isDrawing, onDrawingModeChange]);

  // Handle map load
  const onMapLoad = useCallback((map) => {
    setMap(map);
  }, []);

  // Handle map unload
  const onMapUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Handle polygon click on map: notify parent and fit bounds
  const handlePolygonClick = useCallback((polygon) => {
    setCurrentPolygon(polygon);
    if (typeof onPolygonSelect === 'function') {
      onPolygonSelect(polygon);
    }
    try {
      if (map && polygon?.coordinates && polygon.coordinates.length) {
        const googleMaps = getGoogleMaps();
        if (!googleMaps) return;
        const bounds = new googleMaps.LatLngBounds();
        polygon.coordinates.forEach((pt) => bounds.extend({ lat: pt.lat, lng: pt.lng }));
        map.fitBounds(bounds, 50);
      }
    } catch (e) {
      console.error('Error fitting bounds to polygon:', e);
    }
  }, [map, onPolygonSelect, getGoogleMaps]);

  // Handle info window close
  const handleInfoWindowClose = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  // Handle polygon load
  const onPolygonLoad = useCallback((polygon) => {
    polygonRef.current = polygon;
  }, []);

  // Handle polygon unmount
  const onPolygonUnmount = useCallback(() => {
    polygonRef.current = null;
  }, []);

  const placeTypeColors = useMemo(() => ({
    bus_station: '#607D8B',
    restaurant: '#E91E63',
    gym: '#9C27B0',
    supermarket: '#4CAF50',
    school: '#FFC107',
    subway_station: '#3F51B5'
  }), []);

  const nearbySearchAsync = useCallback((service, request) => {
    return new Promise((resolve) => {
      service.nearbySearch(request, (results, status, pagination) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(results)) {
          resolve(results);
        } else {
          resolve([]);
        }
      });
    });
  }, []);

  const fetchPlaces = useCallback(async () => {
    if (!map || !enabledPlaceTypes || enabledPlaceTypes.length === 0) {
      setPlacesByType({});
      return;
    }
    const googleMaps = getGoogleMaps();
    if (!googleMaps || !googleMaps.places) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const centerLatLng = bounds.getCenter();
    const ne = bounds.getNorthEast();
    const radius = googleMaps.geometry?.spherical?.computeDistanceBetween
      ? googleMaps.geometry.spherical.computeDistanceBetween(centerLatLng, ne)
      : 2000;
    const key = JSON.stringify({
      types: [...enabledPlaceTypes].sort(),
      c: { lat: centerLatLng.lat(), lng: centerLatLng.lng() },
      r: Math.round(radius)
    });
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    const service = new googleMaps.places.PlacesService(map);
    const newPlaces = {};
    for (const type of enabledPlaceTypes) {
      const req = { location: centerLatLng, radius: Math.min(radius, 5000), type };
      // eslint-disable-next-line no-await-in-loop
      const results = await nearbySearchAsync(service, req);
      newPlaces[type] = results || [];
    }
    setPlacesByType(newPlaces);
  }, [map, enabledPlaceTypes, getGoogleMaps, nearbySearchAsync]);

  const onIdle = useCallback(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);
  
  
  // Handle load error
  if (loadError) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        p={2}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error al cargar Google Maps
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          No se pudo cargar el mapa. Por favor, verifica tu conexión a internet o inténtalo de nuevo más tarde.
        </Typography>
      </Box>
    );
  }

  // Return loading state or error state if needed
  if (!isLoaded || apiError) {
    return <MapError error={apiError} width={width} height={height} mapContainerStyle={mapContainerStyle} />;
  }

  if (!isLoaded) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        p={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="textSecondary" align="center">
          Cargando mapa...
        </Typography>
      </Box>
    );
  }

  const placeTypeLabels = {
    bus_station: 'PARADA DE COLECTIVOS',
    restaurant: 'RESTAURANTE',
    gym: 'GIMNASIO',
    supermarket: 'SUPERMERCADO',
    school: 'ESCUELA',
    subway_station: 'ESTACIÓN DE SUBTE'
  };

  return (
    <div style={{ position: 'relative', height, width, ...mapContainerStyle }}>
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={center}
        zoom={zoom}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        onIdle={onIdle}
        onClick={handleMapClick}
        onDblClick={handleMapDblClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          zoomControl: true,
          clickableIcons: false,
          disableDoubleClickZoom: isDrawing,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
        {...rest}
      >
        {/* Drawing preview polygon */}
        {drawingPath.length >= 2 && (
          <Polygon
            path={drawingPath}
            options={drawingPolygonOptions}
          />
        )}
        {/* Drawing preview markers (individual points) */}
        {isDrawing && drawingPath.map((pt, idx) => (
          <Marker
            key={`drawing-pt-${idx}`}
            position={pt}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
              scale: 5,
              fillColor: '#1976d2',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
            clickable={false}
          />
        ))}
        
        {/* Display existing polygons */}
        {polygons.map((polygon, index) => {
          const polygonColor = polygon.color || '#1976d2';
          const isSelected = selectedPolygon?.id === polygon.id;
          const polygonPath = polygon.coordinates || polygon;
          
          return (
            <Polygon
              key={polygon.id || index}
              path={polygonPath}
              options={{
                fillColor: polygonColor,
                fillOpacity: isSelected ? 0.35 : 0.2,
                strokeWeight: isSelected ? 3 : 2,
                strokeColor: polygonColor,
                clickable: true,
                editable: false,
                zIndex: isSelected ? 2 : 1
              }}
              onClick={() => handlePolygonClick(polygon)}
            />
          );
        })}
        
        {/* Markers */}
        {locations.map((location, index) => {
          // Different icon for client markers with channel-based colors
          const icon = location.isClient ? {
            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
            scale: 6,
            fillColor: location.channelColor || '#FF5722',
            fillOpacity: 0.8,
            strokeColor: '#fff',
            strokeWeight: 2,
          } : undefined;
          
          return (
            <Marker 
              key={`marker-${index}`}
              position={location}
              onClick={(event) => handleMarkerClick(location, index, event)}
              title={location.label || `Ubicación ${index + 1}`}
              icon={icon}
            />
          );
        })}

        {/* Google Places markers */}
        {Object.entries(placesByType).flatMap(([type, places]) => {
          const color = placeTypeColors[type] || '#607D8B';
          return places.map((place, idx) => {
            const pos = place.geometry?.location;
            const lat = typeof pos?.lat === 'function' ? pos.lat() : pos?.lat;
            const lng = typeof pos?.lng === 'function' ? pos.lng() : pos?.lng;
            if (lat == null || lng == null) return null;
            const icon = {
              // Upward-pointing triangle centered at (0,0)
              path: 'M 0 -10 L 10 8 L -10 8 Z',
              scale: 1,
              fillColor: color,
              fillOpacity: 0.95,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            };
            return (
              <Marker
                key={`place-${type}-${idx}`}
                position={{ lat, lng }}
                title={place.name || type}
                icon={icon}
                onClick={() => setSelectedPlace({ 
                  position: { lat, lng }, 
                  name: place.name, 
                  type,
                  address: place.vicinity || place.formatted_address,
                  rating: place.rating,
                  total: place.user_ratings_total
                })}
              />
            );
          });
        })}
        
        {selectedPlace && (
          <InfoWindow
            position={selectedPlace.position}
            onCloseClick={() => setSelectedPlace(null)}
          >
            <div style={{ minWidth: 220 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>
                {placeTypeLabels[selectedPlace.type] || selectedPlace.type?.toUpperCase()}
              </div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                {selectedPlace.name || 'Sin nombre'}
              </div>
              {selectedPlace.address && (
                <div style={{ fontSize: 12, marginTop: 2, color: '#666' }}>
                  {selectedPlace.address}
                </div>
              )}
              {typeof selectedPlace.rating === 'number' && (
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  ★ {selectedPlace.rating} {typeof selectedPlace.total === 'number' ? `(${selectedPlace.total})` : ''}
                </div>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
      
      {/* Map Legend */}
      {availableChannels.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          zIndex: 1,
          backgroundColor: 'white',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
          minWidth: '180px',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Canales
          </Typography>
          {availableChannels.map((channel) => (
            <Box key={channel} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                backgroundColor: channelColors[channel] || channelColors['default'] || '#607D8B',
                border: '2px solid white',
                boxShadow: '0 0 2px rgba(0,0,0,0.3)'
              }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{channel}</Typography>
            </Box>
          ))}
        </div>
      )}
      
      {/* Drawing Controls */}
      <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 1,
        backgroundColor: 'white',
        padding: '5px',
        borderRadius: '4px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
      }}>
        <Button
          onClick={toggleDrawing}
          style={{
            padding: '8px 12px',
            backgroundColor: isDrawing ? '#f44336' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title={isDrawing ? 'Cancelar dibujo' : 'Dibujar polígono'}
        >
          {isDrawing ? (
            <>
              <CloseIcon />
              <span>Cancelar</span>
            </>
          ) : (
            <>
              <PolygonIcon />
              <span>Dibujar área</span>
            </>
          )}
        </Button>
        {isDrawing && (
          <div style={{ fontSize: 11, color: '#555', padding: '4px 8px', maxWidth: 180 }}>
            Clic para agregar puntos.<br />
            Doble clic para cerrar el área.
          </div>
        )}
      </div>
    </div>
  );
};

export default Map;
